// El grafo: proyectos, clientes, tareas y presupuestos, y cómo se enganchan.
//
// SVG dibujado a mano. La librería de grafos más liviana pesa más del doble que
// toda la app junta, y el techo de AGENTS.md son 60 KB (PLAN-06, sección 1).
//
// Tres reglas que se ganaron a los golpes y conviene no romper:
//
//   1. El acomodo se calcula UNA vez por vista y queda guardado. Antes se
//      recalculaba con cada toque de zoom: 300 iteraciones de fuerzas sobre
//      todos los nodos para cambiar un ancho de SVG. Ahora el zoom sólo estira
//      el dibujo ya hecho.
//   2. Arrastrar un nodo NO redibuja el SVG entero: mueve ese <g> y corrige a
//      mano las líneas que lo tocan. Con el dedo encima, rearmar el string en
//      cada movimiento se siente pegajoso.
//   3. Tocar un nodo lo elige y muestra qué va antes y qué después. Recién el
//      segundo toque lleva a la ficha: si el primero navegara, no habría forma
//      de mirar el grafo sin irse de la pantalla.

import { api } from "../api";
import { esc } from "../html";
import { acomodar, secuencia, ARISTA_ORDEN } from "../../lib/grafo";
import type { AristaGrafo, NodoGrafo, Posicion } from "../../lib/grafo";

interface Grafo {
  nodos: NodoGrafo[];
  aristas: AristaGrafo[];
}

// Una sola familia: azul para lo importante, pizarra para el resto. Antes cada
// tipo tenía su color de semáforo y el grafo parecía un arbolito de navidad.
const COLOR: Record<string, string> = {
  cliente: "#94A3B8",
  proyecto: "#3B82F6",
  tarea: "#64748B",
  presupuesto: "#CBD5E1",
  bitacora: "#7DA6E8",
};

// El único color de alarma de la paleta. Se usa para lo vencido y nada más:
// cuando todo grita, no se escucha nada (tailwind.config.js).
const ALERTA = "#CB6A5C";
const LINEA = "#393939";

const RADIO: Record<string, number> = {
  cliente: 13,
  proyecto: 15,
  tarea: 8,
  presupuesto: 9,
  bitacora: 7,
};

const TIPOS = ["proyecto", "cliente", "tarea", "presupuesto", "bitacora"] as const;

const ETIQUETA_TIPO: Record<string, string> = {
  cliente: "Clientes",
  proyecto: "Proyectos",
  tarea: "Tareas",
  presupuesto: "Presupuestos",
  bitacora: "Notas",
};

/** Estados que se pintan con el color de alarma. */
const VENCIDOS = new Set(["VENCIDA", "VENCIDO"]);
/** Estados de "esto ya arrancó": se pintan llenos, no huecos. */
const EN_MARCHA = new Set(["EN_CURSO", "EN_DESARROLLO", "EN_PRODUCCION", "ACEPTADO"]);

/** Pasado este tamaño, sólo se etiqueta lo grande: si no, los textos se pisan. */
const TOPE_ETIQUETAS = 45;

/** Los títulos largos no se leen a este tamaño: se cortan. */
function corto(texto: string, largo = 22): string {
  return texto.length > largo ? texto.slice(0, largo - 1) + "…" : texto;
}

/**
 * Deja sólo un proyecto y lo que cuelga de él: su cliente, sus tareas, sus
 * presupuestos, sus notas y los proyectos con los que está vinculado.
 *
 * Un salto y no más: si trajera también las tareas de los proyectos vecinos,
 * dejaría de ser un foco y volvería a ser la madeja.
 */
function soloEsteProyecto(g: Grafo, foco: string): Grafo {
  const cerca = new Set([foco]);
  for (const a of g.aristas) {
    if (a.origen === foco) cerca.add(a.destino);
    if (a.destino === foco) cerca.add(a.origen);
  }
  return {
    nodos: g.nodos.filter((n) => cerca.has(n.id)),
    // Se quedan también las aristas entre vecinos: así se sigue viendo qué
    // tarea del proyecto frena a cuál.
    aristas: g.aristas.filter((a) => cerca.has(a.origen) && cerca.has(a.destino)),
  };
}

export async function renderGrafo(
  contenedor: HTMLElement,
  params: Record<string, string>,
): Promise<void> {
  // Las tareas se pueden apagar: con muchas, el grafo se vuelve una madeja.
  const visibles = new Set<string>(TIPOS);
  let zoom = 1;
  let grafo: Grafo = { nodos: [], aristas: [] };
  let foco = params.id ? `proyecto:${Number(params.id)}` : "";
  let elegido = "";

  // Lo que se está dibujando ahora, ya acomodado. Se rehace sólo cuando cambia
  // el foco o los filtros, nunca por zoom ni por elegir un nodo.
  let vista = {
    clave: "",
    nodos: [] as NodoGrafo[],
    aristas: [] as AristaGrafo[],
    posiciones: new Map<string, Posicion>(),
    ancho: 0,
    alto: 0,
  };

  function recalcular(): void {
    const clave = `${foco}|${[...visibles].sort().join(",")}`;
    if (clave === vista.clave) return;

    const base = foco ? soloEsteProyecto(grafo, foco) : grafo;
    const nodos = base.nodos.filter((n) => visibles.has(n.tipo));
    const presentes = new Set(nodos.map((n) => n.id));
    const aristas = base.aristas.filter(
      (a) => presentes.has(a.origen) && presentes.has(a.destino),
    );
    const { posiciones, ancho, alto } = acomodar(nodos, aristas);
    vista = { clave, nodos, aristas, posiciones, ancho, alto };

    // Si el elegido se fue con el filtro, deja de estar elegido.
    if (elegido && !presentes.has(elegido)) elegido = "";
  }

  const buscar = (id: string): NodoGrafo | undefined => vista.nodos.find((n) => n.id === id);

  /** El color con el que se pinta un nodo: su tipo, salvo que esté vencido. */
  function colorDe(n: NodoGrafo): string {
    if (n.estado && VENCIDOS.has(n.estado)) return ALERTA;
    return COLOR[n.tipo] ?? "#64748B";
  }

  /** Vecinos directos del elegido, para poder apagar todo lo demás. */
  function cercanos(): Set<string> {
    if (!elegido) return new Set();
    const s = new Set([elegido]);
    for (const a of vista.aristas) {
      if (a.origen === elegido) s.add(a.destino);
      if (a.destino === elegido) s.add(a.origen);
    }
    return s;
  }

  function svg(): string {
    const cerca = cercanos();
    const apagar = elegido !== "";
    const muchos = vista.nodos.length > TOPE_ETIQUETAS;

    const lineas = vista.aristas
      .map((a, i) => {
        const o = vista.posiciones.get(a.origen);
        const d = vista.posiciones.get(a.destino);
        if (!o || !d) return "";
        const orden = a.tipo === ARISTA_ORDEN;
        const tocada = !apagar || (cerca.has(a.origen) && cerca.has(a.destino));
        return `<line data-arista="${i}" data-o="${esc(a.origen)}" data-d="${esc(a.destino)}"
                  x1="${o.x}" y1="${o.y}" x2="${d.x}" y2="${d.y}"
                  stroke="${orden ? ALERTA : LINEA}"
                  stroke-width="${orden ? 2 : 1.5}"
                  stroke-opacity="${tocada ? 1 : 0.12}"
                  ${orden ? 'stroke-dasharray="5 3" marker-end="url(#flecha-orden)"' : ""} />`;
      })
      .join("");

    const circulos = vista.nodos
      .map((n) => {
        const p = vista.posiciones.get(n.id);
        if (!p) return "";
        const r = RADIO[n.tipo] ?? 10;
        const color = colorDe(n);
        const esElegido = n.id === elegido;
        const tenue = apagar && !cerca.has(n.id);
        const lleno = n.estado && EN_MARCHA.has(n.estado);
        const trabada = n.estado === "BLOQUEADA";
        // La etiqueta se muestra siempre que quepa; con el grafo grande, sólo
        // en lo que se lee de lejos y en lo que se está mirando.
        const conTexto =
          !muchos || esElegido || cerca.has(n.id) || n.tipo === "proyecto" || n.tipo === "cliente";
        return `<g class="nodo" data-id="${esc(n.id)}" data-hash="${esc(n.hash)}"
                   opacity="${tenue ? 0.22 : 1}" style="cursor:grab;touch-action:none">
            <title>${esc(n.etiqueta)}${n.estado ? ` · ${esc(n.estado.toLowerCase())}` : ""}</title>
            ${esElegido ? `<circle cx="${p.x}" cy="${p.y}" r="${r + 7}" fill="${color}" fill-opacity="0.14" />` : ""}
            <circle cx="${p.x}" cy="${p.y}" r="${r}"
                    fill="${color}" fill-opacity="${lleno ? 0.55 : 0.18}"
                    stroke="${color}" stroke-width="${esElegido ? 3 : 2}"
                    ${trabada ? 'stroke-dasharray="3 2"' : ""} />
            ${
              conTexto
                ? `<text x="${p.x}" y="${p.y + r + 13}" text-anchor="middle" font-size="11"
                         fill="${esElegido ? "#FFFFFF" : "#E6E8EC"}"
                         font-weight="${esElegido ? "600" : "400"}"
                         style="pointer-events:none">${esc(corto(n.etiqueta))}</text>`
                : ""
            }
          </g>`;
      })
      .join("");

    // La flecha se dibuja en el color de alarma y se corre hacia atrás para no
    // quedar tapada por el círculo del nodo al que apunta.
    return `<svg width="${Math.round(vista.ancho * zoom)}" height="${Math.round(vista.alto * zoom)}"
                 viewBox="0 0 ${vista.ancho} ${vista.alto}" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <marker id="flecha-orden" viewBox="0 0 10 10" refX="16" refY="5"
                  markerWidth="5" markerHeight="5" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="${ALERTA}" />
          </marker>
        </defs>
        <g id="aristas">${lineas}</g><g id="nodos">${circulos}</g>
      </svg>`;
  }

  /** El panel de abajo: qué es el elegido, qué va antes y qué se destraba. */
  function panel(): string {
    if (!elegido) {
      return `<p class="mt-2 text-xs text-apagado">
          Tocá un nodo para ver qué lo frena y qué destraba. Tocalo de nuevo para abrir su ficha.
          Arrastrá un nodo para desenredar; con dos dedos, acercás.
        </p>`;
    }
    const n = buscar(elegido);
    if (!n) return "";
    const { antes, despues } = secuencia(vista.aristas, elegido);
    const chip = (id: string): string => {
      const v = buscar(id);
      if (!v) return "";
      // normal-case: .chip va en mayúsculas y un título de tarea así no se lee.
      return `<button class="salto chip normal-case" data-id="${esc(id)}">
          ${esc(corto(v.etiqueta, 26))}
        </button>`;
    };
    const linea = (titulo: string, ids: string[], vacio: string): string =>
      `<p class="mt-2 text-xs text-apagado">${titulo}</p>
       <div class="mt-1 flex flex-wrap gap-1">
         ${ids.length ? ids.map(chip).join("") : `<span class="text-xs text-apagado/70">${vacio}</span>`}
       </div>`;

    return `<div class="tarjeta mt-3">
        <div class="flex items-start justify-between gap-3">
          <div>
            <p class="font-medium">${esc(n.etiqueta)}</p>
            <p class="text-xs text-apagado">
              ${esc(ETIQUETA_TIPO[n.tipo] ?? n.tipo)}${n.estado ? ` · ${esc(n.estado.toLowerCase())}` : ""}
            </p>
          </div>
          ${n.hash ? `<button id="abrir" class="btn-sec shrink-0 px-3 py-1 text-sm">Abrir ficha</button>` : ""}
        </div>
        ${linea("Primero hay que terminar", antes, "nada la frena: se puede hacer ya")}
        ${linea("Y entonces se destraba", despues, "no frena a ninguna otra")}
      </div>`;
  }

  function dibujar(): void {
    recalcular();
    const lienzo = contenedor.querySelector("#lienzo") as HTMLElement;
    lienzo.innerHTML = vista.nodos.length
      ? svg()
      : `<p class="p-8 text-center text-apagado">No hay nada para mostrar con esos filtros.</p>`;

    (contenedor.querySelector("#detalle") as HTMLElement).innerHTML = panel();
    (contenedor.querySelector("#cuenta") as HTMLElement).textContent =
      `${vista.nodos.length} nodos · ${vista.aristas.length} vínculos`;

    conectarNodos(lienzo);
    conectarPanel();
  }

  /** Sólo cambia el tamaño del SVG: el acomodo ya está hecho. */
  function aplicarZoom(): void {
    const s = contenedor.querySelector("#lienzo svg") as SVGSVGElement | null;
    if (!s) return;
    s.setAttribute("width", String(Math.round(vista.ancho * zoom)));
    s.setAttribute("height", String(Math.round(vista.alto * zoom)));
  }

  function conectarNodos(lienzo: HTMLElement): void {
    lienzo.querySelectorAll<SVGGElement>(".nodo").forEach((g) => {
      const id = g.dataset.id as string;
      let arrastro = false;
      let desde = { x: 0, y: 0 };

      g.addEventListener("pointerdown", (ev) => {
        ev.preventDefault();
        // Si el dedo ya se levantó, capturar tira NotFoundError y se lleva
        // puesto el toque entero. No pasa nada si no se puede capturar: sin
        // captura el arrastre no sigue al dedo fuera del nodo, y listo.
        try {
          g.setPointerCapture(ev.pointerId);
        } catch {
          /* el puntero ya no está */
        }
        arrastro = false;
        desde = { x: ev.clientX, y: ev.clientY };
      });

      g.addEventListener("pointermove", (ev) => {
        if (!g.hasPointerCapture(ev.pointerId)) return;
        const dx = ev.clientX - desde.x;
        const dy = ev.clientY - desde.y;
        // Un dedo tiembla: hasta 4 px sigue siendo un toque, no un arrastre.
        if (!arrastro && Math.abs(dx) + Math.abs(dy) < 4) return;
        arrastro = true;
        desde = { x: ev.clientX, y: ev.clientY };
        const p = vista.posiciones.get(id);
        if (!p) return;
        p.x += dx / zoom;
        p.y += dy / zoom;
        moverNodo(lienzo, id, p);
      });

      g.addEventListener("pointerup", (ev) => {
        if (g.hasPointerCapture(ev.pointerId)) g.releasePointerCapture(ev.pointerId);
        if (arrastro) return; // fue un arrastre, no un toque
        // Primer toque elige; el segundo, sobre el mismo, abre la ficha.
        if (elegido === id) {
          const hash = g.dataset.hash;
          if (hash) location.hash = hash;
          return;
        }
        elegido = id;
        dibujar();
      });
    });
  }

  /** Mueve un nodo ya dibujado y corrige las líneas que lo tocan. */
  function moverNodo(lienzo: HTMLElement, id: string, p: Posicion): void {
    const g = lienzo.querySelector<SVGGElement>(`.nodo[data-id="${CSS.escape(id)}"]`);
    if (!g) return;
    g.querySelectorAll("circle").forEach((c) => {
      c.setAttribute("cx", String(p.x));
      c.setAttribute("cy", String(p.y));
    });
    const t = g.querySelector("text");
    if (t) {
      const r = Number(g.querySelector("circle")?.getAttribute("r") ?? 10);
      t.setAttribute("x", String(p.x));
      t.setAttribute("y", String(p.y + r + 13));
    }
    lienzo.querySelectorAll<SVGLineElement>("#aristas line").forEach((l) => {
      if (l.dataset.o === id) {
        l.setAttribute("x1", String(p.x));
        l.setAttribute("y1", String(p.y));
      }
      if (l.dataset.d === id) {
        l.setAttribute("x2", String(p.x));
        l.setAttribute("y2", String(p.y));
      }
    });
  }

  function conectarPanel(): void {
    contenedor.querySelector("#abrir")?.addEventListener("click", () => {
      const n = buscar(elegido);
      if (n?.hash) location.hash = n.hash;
    });
    contenedor.querySelectorAll<HTMLButtonElement>(".salto").forEach((b) => {
      b.addEventListener("click", () => {
        elegido = b.dataset.id as string;
        dibujar();
        // Que el nodo al que saltó quede a la vista, no fuera de la pantalla.
        const p = vista.posiciones.get(elegido);
        const lienzo = contenedor.querySelector("#lienzo") as HTMLElement;
        if (p) {
          lienzo.scrollTo({
            left: p.x * zoom - lienzo.clientWidth / 2,
            top: p.y * zoom - lienzo.clientHeight / 2,
            behavior: "smooth",
          });
        }
      });
    });
  }

  function pintar(): void {
    contenedor.innerHTML = `
      <h1 class="titulo-pagina">Grafo</h1>
      <p id="cuenta" class="subtitulo"></p>

      <div class="mt-3 flex items-center gap-2">
        <select id="foco" class="campo flex-1">
          <option value="">Todo junto</option>
          ${grafo.nodos
            .filter((n) => n.tipo === "proyecto")
            .sort((a, b) => a.etiqueta.localeCompare(b.etiqueta))
            .map(
              (n) =>
                `<option value="${n.id}"${n.id === foco ? " selected" : ""}>Sólo ${esc(n.etiqueta)}</option>`,
            )
            .join("")}
        </select>
        <span class="flex shrink-0 items-center gap-1">
          <button id="menos" class="btn-sec px-3 py-1" aria-label="Alejar">−</button>
          <button id="mas" class="btn-sec px-3 py-1" aria-label="Acercar">+</button>
        </span>
      </div>

      <div class="mt-2 flex flex-wrap items-center gap-2">
        ${TIPOS.map(
          (t) =>
            `<button class="fTipo chip ${visibles.has(t) ? "chip-acento" : ""}" data-tipo="${t}">
               ${ETIQUETA_TIPO[t]}
             </button>`,
        ).join("")}
      </div>

      <div id="lienzo" class="mt-3 max-h-[70vh] overflow-auto rounded-2xl border border-borde bg-superficie"></div>

      <div class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-apagado">
        ${TIPOS.map(
          (t) =>
            `<span class="inline-flex items-center gap-1.5">
               <span class="inline-block h-2.5 w-2.5 rounded-full border-2" style="border-color:${COLOR[t]}"></span>
               ${ETIQUETA_TIPO[t]}
             </span>`,
        ).join("")}
        <span class="inline-flex items-center gap-1.5">
          <span class="inline-block h-2.5 w-2.5 rounded-full border-2" style="border-color:${ALERTA}"></span>
          Vencido
        </span>
        <span class="inline-flex items-center gap-1.5">
          <svg width="26" height="8" aria-hidden="true"><line x1="0" y1="4" x2="24" y2="4"
            stroke="${ALERTA}" stroke-width="2" stroke-dasharray="5 3" /></svg>
          Frena a la que apunta
        </span>
      </div>

      <div id="detalle"></div>`;

    contenedor.querySelectorAll<HTMLButtonElement>(".fTipo").forEach((b) => {
      b.addEventListener("click", () => {
        const t = b.dataset.tipo as string;
        if (visibles.has(t)) visibles.delete(t);
        else visibles.add(t);
        b.classList.toggle("chip-acento", visibles.has(t));
        dibujar();
      });
    });

    // Cambiar el foco cambia el hash: así el grafo de un proyecto se puede
    // marcar, compartir y volver con el botón de atrás.
    contenedor.querySelector("#foco")?.addEventListener("change", (ev) => {
      const v = (ev.target as HTMLSelectElement).value;
      foco = v;
      elegido = "";
      location.hash = v ? `#/grafo/${v.split(":")[1]}` : "#/grafo";
      dibujar();
    });

    const acercar = (factor: number): void => {
      zoom = Math.min(Math.max(zoom * factor, 0.4), 4);
      aplicarZoom();
    };
    contenedor.querySelector("#mas")?.addEventListener("click", () => acercar(1.3));
    contenedor.querySelector("#menos")?.addEventListener("click", () => acercar(1 / 1.3));

    const lienzo = contenedor.querySelector("#lienzo") as HTMLElement;

    // Rueda del mouse: acerca en vez de scrollear, que es lo que se espera de
    // un lienzo. Con la tecla puesta, se deja scrollear normal.
    lienzo.addEventListener(
      "wheel",
      (ev) => {
        if (ev.ctrlKey || ev.shiftKey) return;
        ev.preventDefault();
        acercar(ev.deltaY < 0 ? 1.12 : 1 / 1.12);
      },
      { passive: false },
    );

    // Pellizcar con dos dedos. Con uno solo no se toca nada: ese gesto es el
    // scroll del contenedor, que lo hace el navegador y sale gratis.
    let pellizco = 0;
    const distancia = (t: TouchList): number =>
      Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

    lienzo.addEventListener(
      "touchstart",
      (ev) => {
        if (ev.touches.length === 2) pellizco = distancia(ev.touches);
      },
      { passive: true },
    );
    lienzo.addEventListener(
      "touchmove",
      (ev) => {
        if (ev.touches.length !== 2 || !pellizco) return;
        ev.preventDefault();
        const ahora = distancia(ev.touches);
        acercar(ahora / pellizco);
        pellizco = ahora;
      },
      { passive: false },
    );
    lienzo.addEventListener("touchend", () => {
      pellizco = 0;
    });

    dibujar();
  }

  try {
    grafo = await api.get<Grafo>("/api/grafo");
    pintar();
  } catch (e) {
    contenedor.innerHTML = `<div class="tarjeta text-alerta">No se pudo cargar el grafo: ${esc(
      e instanceof Error ? e.message : "error desconocido",
    )}</div>`;
  }
}
